/**
 * localStorage-backed eligibility store for the Rovo Chat CSAT survey.
 *
 * The survey is only shown when BOTH conditions are satisfied since the user's
 * last interaction with the survey:
 *
 *   1. A 30-day cooldown has elapsed since the last interaction.
 *   2. The user has started at least 3 new conversations and sent at least
 *      one valid (user-authored, non-empty) message in each.
 */

import { AtlBrowserStorageLocal } from '@atlassian/browser-storage-controls';

const STORAGE_KEY = 'rovoChatCsatEligibility';
const COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;
const MIN_QUALIFYING_CONVERSATIONS = 3;

type CsatEligibilityData = {
	lastShownAt: number;
	qualifyingConversations: number;
};

const DEFAULTS: CsatEligibilityData = {
	lastShownAt: 0,
	qualifyingConversations: 0,
};

let cached: CsatEligibilityData | null = null;

function isPlainObject(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isCsatEligibilityData(value: unknown): value is CsatEligibilityData {
	return (
		isPlainObject(value) &&
		typeof value.lastShownAt === 'number' &&
		typeof value.qualifyingConversations === 'number'
	);
}

function readFromStorage(): CsatEligibilityData {
	if (cached) {
		return cached;
	}
	try {
		const raw = AtlBrowserStorageLocal.getItem(STORAGE_KEY);
		if (raw) {
			const parsed: unknown = JSON.parse(raw);
			if (isCsatEligibilityData(parsed)) {
				cached = parsed;
				return parsed;
			}
		}
	} catch {
		// Storage unavailable or JSON corrupt — fall through to defaults.
	}
	cached = DEFAULTS;
	return DEFAULTS;
}

function writeToStorage(data: CsatEligibilityData): void {
	cached = data;
	try {
		AtlBrowserStorageLocal.setItem(STORAGE_KEY, JSON.stringify(data));
	} catch {
		// Best-effort: storage might be denied (no consent) or full.
	}
}

/**
 * True only when both the 30-day cooldown has elapsed AND the user has had
 * at least `MIN_QUALIFYING_CONVERSATIONS` qualifying new conversations since
 * the last survey interaction.
 */
export const isCsatEligible = (): boolean => {
	const { lastShownAt, qualifyingConversations } = readFromStorage();
	if (lastShownAt && Date.now() - lastShownAt < COOLDOWN_MS) {
		return false;
	}
	return qualifyingConversations >= MIN_QUALIFYING_CONVERSATIONS;
};

/**
 * Call when the user sends a valid message that starts a brand-new
 * conversation.
 */
export const recordValidMessage = (): void => {
	const cur = readFromStorage();
	if (cur.qualifyingConversations >= MIN_QUALIFYING_CONVERSATIONS) {
		return;
	}
	writeToStorage({
		...cur,
		qualifyingConversations: cur.qualifyingConversations + 1,
	});
};

/**
 * Call when the user interacts with the survey (submit, skip, or dismiss).
 */
export const recordCsatInteracted = (): void => {
	writeToStorage({
		lastShownAt: Date.now(),
		qualifyingConversations: 0,
	});
};

/** Test/debug helper: wipe the persisted eligibility record. */
export const clearCsatEligibility = (): void => {
	cached = null;
	try {
		AtlBrowserStorageLocal.removeItem(STORAGE_KEY);
	} catch {
		// Best-effort.
	}
};

/**
 * Test-only helper: reset the module-scoped in-memory cache. Not part of the
 * public API; call from `beforeEach` in tests.
 */
export const __resetCsatEligibilityForTests = (): void => {
	cached = null;
};
